import React from "react";

// Status hues carry fixed meanings across the ticket UI:
//   slate = untouched, violet = we are on it, blue = booked,
//   amber = blocked on someone, red = stopped, emerald = done.
const STATUS_CONFIG = {
  new: { dot: "bg-slate-500", bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", label: "New" },
  in_progress: { dot: "bg-violet-700", bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", label: "In Progress" },
  scheduled: { dot: "bg-blue-600", bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", label: "Scheduled" },
  waiting_vendor: { dot: "bg-amber-600", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", label: "Waiting: Vendor" },
  waiting_approval: { dot: "bg-amber-600", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", label: "Waiting: Approval" },
  waiting_submitter: { dot: "bg-amber-600", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", label: "Waiting: Submitter" },
  completed: { dot: "bg-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", label: "Completed" },
  closed: { dot: "bg-slate-400", bg: "bg-slate-100", border: "border-slate-200", text: "text-slate-600", label: "Closed" },
};

// Approval outcomes fold into the status pill rather than rendering a second badge.
const APPROVAL_CONFIG = {
  approved: { dot: "bg-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", label: "Approved" },
  rejected: { dot: "bg-red-600", bg: "bg-red-50", border: "border-red-200", text: "text-red-800", label: "Rejected" },
};

const PRIORITY_CONFIG = {
  low: { text: "text-slate-400", label: "Low" },
  normal: { text: "text-slate-400", label: "Normal" },
  high: { text: "text-orange-600", label: "High", icon: "high" },
  urgent: { text: "text-red-600", label: "Urgent", icon: "urgent" },
};

const ISSUE_TYPE_CONFIG = {
  // Maintenance
  electrical_lighting: { label: "Electrical/Lighting" },
  plumbing: { label: "Plumbing" },
  damage: { label: "Damage" },
  hvac: { label: "HVAC" },
  equipment: { label: "Equipment" },
  furniture: { label: "Furniture" },
  cleaning: { label: "Cleaning" },
  safety: { label: "Safety" },
  other: { label: "Other" },
  // IT
  hardware: { label: "Hardware" },
  software: { label: "Software" },
  network: { label: "Network" },
  access_permissions: { label: "Access/Permissions" },
  printer: { label: "Printer" },
  phone_system: { label: "Phone System" },
  // Marketing
  signage: { label: "Signage" },
  printing: { label: "Printing" },
  social_media: { label: "Social Media" },
  promotion: { label: "Promotion" },
  branding: { label: "Branding" },
  event: { label: "Event" },
  // Menu
  new_item: { label: "New Item" },
  price_change: { label: "Price Change" },
  text_edit: { label: "Text Edit (Descriptions/Names/etc)" },
  remove_item: { label: "Remove Item" },
  seasonal_changeover: { label: "Seasonal Changeover" },
  allergen_dietary: { label: "Allergen/Dietary Update" },
  toast_update: { label: "Toast Update" },
  private_dining: { label: "Private Dining Update" },
};

function Pill({ c }) {
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full pl-2 pr-2.5 py-0.5 ${c.bg} border ${c.border} whitespace-nowrap`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      <span className={`text-[11px] font-semibold leading-4 ${c.text}`}>{c.label}</span>
    </div>
  );
}

const TERMINAL_STATUSES = ["completed", "closed"];

// The pill always shows the workflow status. The approval outcome (approved or
// rejected) is a caption beneath it while the ticket is still open; once the
// ticket is completed or closed the outcome is history and the caption is hidden.
export function resolveStatusConfig(status) {
  return STATUS_CONFIG[status] || STATUS_CONFIG.new;
}

// `label` is the ticket category's wording for the status (menu tickets
// relabel them); colours stay keyed to the stored value.
export function resolveStatusLabel(status, label) {
  return label || resolveStatusConfig(status).label;
}

const APPROVAL_CAPTION = {
  approved: { text: "text-emerald-700", label: "Approved", path: "M5 12l5 5L19 7" },
  rejected: { text: "text-red-700", label: "Rejected", path: "M6 6l12 12M18 6L6 18" },
};

export function StatusBadge({ status, approvalStatus, label }) {
  const caption =
    !TERMINAL_STATUSES.includes(status) && APPROVAL_CAPTION[approvalStatus];
  const config = resolveStatusConfig(status);
  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <Pill c={label ? { ...config, label } : config} />
      {caption && (
        <span className={`inline-flex items-center gap-1 pl-2 text-[11px] font-medium whitespace-nowrap ${caption.text}`}>
          <svg width="10" height="10" viewBox="4 4 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d={caption.path} />
          </svg>
          {caption.label}
        </span>
      )}
    </div>
  );
}

// Kept for callers that still render approval separately; the list folds it into StatusBadge.
export function ApprovalBadge({ approvalStatus }) {
  if (!APPROVAL_CONFIG[approvalStatus]) return null;
  return <Pill c={APPROVAL_CONFIG[approvalStatus]} />;
}

// `label` lets callers pass the company's own priority wording (from
// get_ticket_issue_types); colour and icon stay keyed to the stored value.
export function PriorityBadge({ priority, label }) {
  const c = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
  const emphasized = Boolean(c.icon);
  return (
    <div className={`inline-flex items-center gap-1 whitespace-nowrap text-[13px] ${emphasized ? "font-semibold" : ""} ${c.text}`}>
      {c.icon === "urgent" && (
        <svg width="5" height="11" viewBox="9.5 3.5 5 17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M12 4v10M12 19v1" />
        </svg>
      )}
      {c.icon === "high" && (
        <svg width="10" height="6" viewBox="5 7 14 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 14l6-6 6 6" />
        </svg>
      )}
      <span>{label || c.label}</span>
    </div>
  );
}

export function IssueTypeBadge({ issueType, label }) {
  const text = ISSUE_TYPE_CONFIG[issueType]?.label || label || issueType;
  return <span className="text-[13px] text-slate-700 whitespace-nowrap">{text}</span>;
}

export const ISSUE_TYPE_OPTIONS_MAINTENANCE = [
  { value: "electrical_lighting", label: "Electrical/Lighting" },
  { value: "plumbing", label: "Plumbing" },
  { value: "damage", label: "Damage" },
  { value: "hvac", label: "HVAC" },
  { value: "equipment", label: "Equipment" },
  { value: "furniture", label: "Furniture" },
  { value: "cleaning", label: "Cleaning" },
  { value: "safety", label: "Safety" },
  { value: "other", label: "Other" },
];

export const ISSUE_TYPE_OPTIONS_IT = [
  { value: "hardware", label: "Hardware" },
  { value: "software", label: "Software" },
  { value: "network", label: "Network" },
  { value: "access_permissions", label: "Access/Permissions" },
  { value: "printer", label: "Printer" },
  { value: "phone_system", label: "Phone System" },
  { value: "other", label: "Other" },
];

export const ISSUE_TYPE_OPTIONS_MARKETING = [
  { value: "signage", label: "Signage" },
  { value: "printing", label: "Printing" },
  { value: "social_media", label: "Social Media" },
  { value: "promotion", label: "Promotion" },
  { value: "branding", label: "Branding" },
  { value: "event", label: "Event" },
  { value: "other", label: "Other" },
];

export const ISSUE_TYPE_OPTIONS_MENU = [
  { value: "new_item", label: "New Item" },
  { value: "price_change", label: "Price Change" },
  { value: "text_edit", label: "Text Edit (Descriptions/Names/etc)" },
  { value: "remove_item", label: "Remove Item" },
  { value: "seasonal_changeover", label: "Seasonal Changeover" },
  { value: "allergen_dietary", label: "Allergen/Dietary Update" },
  { value: "toast_update", label: "Toast Update" },
  { value: "private_dining", label: "Private Dining Update" },
  { value: "other", label: "Other" },
];

export const CATEGORY_ISSUE_TYPE_MAP = {
  maintenance: ISSUE_TYPE_OPTIONS_MAINTENANCE,
  it: ISSUE_TYPE_OPTIONS_IT,
  marketing: ISSUE_TYPE_OPTIONS_MARKETING,
  menu: ISSUE_TYPE_OPTIONS_MENU,
};

// Union of all issue types (for backward compat)
export const ISSUE_TYPE_OPTIONS = ISSUE_TYPE_OPTIONS_MAINTENANCE;

export const CATEGORY_CONFIG = {
  maintenance: { label: "Maintenance" },
  it: { label: "IT" },
  marketing: { label: "Marketing" },
  menu: { label: "Menu" },
};

export const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "scheduled", label: "Scheduled" },
  { value: "waiting_vendor", label: "Waiting: Vendor" },
  { value: "waiting_approval", label: "Waiting: Approval" },
  { value: "waiting_submitter", label: "Waiting: Submitter" },
  { value: "completed", label: "Completed" },
  { value: "closed", label: "Closed" },
];

export const STATUS_OPTIONS_FORM = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "scheduled", label: "Scheduled" },
  { value: "waiting_vendor", label: "Waiting: Vendor" },
  { value: "waiting_approval", label: "Waiting: Approval" },
  { value: "waiting_submitter", label: "Waiting: Submitter" },
];

// Built-in status wording per category, used until (or if) the options
// endpoint answers. Menu tickets relabel statuses and never wait on a vendor;
// the backend's get_statuses_for_category is the source of truth.
const CATEGORY_STATUS_OPTIONS = {
  menu: [
    { value: "new", label: "Submitted" },
    { value: "in_progress", label: "In Review" },
    { value: "waiting_submitter", label: "Needs Info" },
    { value: "waiting_approval", label: "Waiting: Approval" },
    { value: "scheduled", label: "Scheduled to Go Live" },
    { value: "completed", label: "Live" },
    { value: "closed", label: "Canceled" },
  ],
};

export function getDefaultStatusOptions(category) {
  return CATEGORY_STATUS_OPTIONS[category] || STATUS_OPTIONS;
}

// Open (non-terminal) statuses from a category's status options, for forms.
export function openStatusOptions(statusOptions) {
  return (statusOptions || STATUS_OPTIONS).filter((o) => !TERMINAL_STATUSES.includes(o.value));
}

// Fallback for the menus a menu change can touch (served as menu_options).
export const MENU_OPTIONS = [
  { value: "dinner", label: "Dinner" },
  { value: "lunch", label: "Lunch" },
  { value: "brunch", label: "Brunch" },
  { value: "happy_hour", label: "Happy Hour" },
  { value: "toast_only", label: "Toast Only" },
];

export const RECURRENCE_INTERVAL_OPTIONS = [
  { value: "daily", label: "Day(s)" },
  { value: "weekly", label: "Week(s)" },
  { value: "monthly", label: "Month(s)" },
];

// Menu values from a ticket's menu_details.menus. Tickets saved before menus
// became a multi-select hold free text ("Dinner, Happy Hour"); map its known
// menus onto option values instead of dropping them.
export function normalizeMenus(value, options = MENU_OPTIONS) {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  const key = (v) => String(v).toLowerCase().replace(/[\s_]+/g, "");
  const lookup = {};
  options.forEach((o) => {
    lookup[key(o.value)] = o.value;
    lookup[key(o.label)] = o.value;
  });
  const values = [];
  value.split(/,|;|\/|&|\band\b|\n/i).forEach((piece) => {
    const match = lookup[key(piece)] || lookup[key(piece.replace(/\bmenus?\b/gi, ""))];
    if (match && !values.includes(match)) values.push(match);
  });
  return options.map((o) => o.value).filter((v) => values.includes(v));
}

// Menu fields for editing: menus as option values, and any old free-text
// menus that don't match an option kept in menus_other (the backend does the
// same), so saving never drops what was typed.
export function withLegacyMenus(details, options = MENU_OPTIONS) {
  const d = details || {};
  const menus = normalizeMenus(d.menus, options);
  if (d.menus_other || typeof d.menus !== "string" || !d.menus.trim()) {
    return { ...d, menus };
  }
  const key = (v) => String(v).toLowerCase().replace(/[\s_]+/g, "");
  const known = new Set(options.flatMap((o) => [key(o.value), key(o.label)]));
  const unmatched = d.menus
    .split(/,|;|\/|&|\band\b|\n/i)
    .map((p) => p.trim())
    .filter((p) => p && !known.has(key(p)) && !known.has(key(p.replace(/\bmenus?\b/gi, ""))));
  return unmatched.length ? { ...d, menus, menus_other: unmatched.join(", ") } : { ...d, menus };
}
